import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { DarkModeToggle } from './DarkModeToggle';
import { Logo } from './Logo';

import { mergeClasses } from '../utils/mergeClasses';

import { type IPage } from '../models/pages';
import { type AppSection } from '../pages/Section';
import { NavBar } from './NavBar';
import { OrgDropDown } from './OrgDropDown';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';

export interface HeaderProps {
  activeSection: AppSection;
  pages: { pages: IPage[]; id: AppSection }[];
}

export function MobileHeader({ activeSection, pages }: HeaderProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState(activeSection === 'guide');
  const [referenceExpanded, setReferenceExpanded] = useState(
    activeSection === 'reference'
  );

  const handleGuideClick = (e: MouseEvent) => {
    setGuideExpanded((prev) => !prev);
    e.preventDefault();
  };

  const handleReferenceClick = (e: MouseEvent) => {
    setReferenceExpanded((prev) => !prev);
    e.preventDefault();
  };

  return (
    <div>
      <header className="mobile-header">
        <div className="lg:align-center mx-4 flex flex-row items-center justify-between gap-6">
          <Link to="/" className="flex flex-row items-center gap-2">
            <Logo className="size-14 text-white" />
            <span>for Developers</span>
          </Link>
          <p
            data-testid="mobile-menu"
            className="text-2xl text-inpay-green-secondary-light-1000"
            onClick={() => setShowMenu(!showMenu)}
          >
            Menu
          </p>
        </div>
      </header>
      <div
        className={mergeClasses(
          'mobile-menu-container bg-inpay-dark-green-1000 text-white',
          showMenu ? '' : 'hidden'
        )}
      >
        <div className="flex flex-col justify-center gap-6 p-6">
          <div onClick={handleGuideClick}>
            <div className="mobile-subheader flex flex-row items-center justify-between">
              <p
                className={mergeClasses(
                  'text-2xl',
                  activeSection === 'guide'
                    ? 'text-inpay-green-secondary-light-1000'
                    : ''
                )}
              >
                Documentation
              </p>
              {guideExpanded ? (
                <ChevronUpIcon width={20} height={20} />
              ) : (
                <ChevronDownIcon width={20} height={20} />
              )}
            </div>
            {guideExpanded && (
              <NavBar
                pages={pages.find((x) => x.id === 'guide')?.pages ?? []}
                parentSection="guide"
                mobileCallback={() => setShowMenu(false)}
              />
            )}
          </div>
          <div onClick={handleReferenceClick}>
            <div className="mobile-subheader flex flex-row items-center justify-between">
              <p
                className={mergeClasses(
                  'text-2xl',
                  activeSection === 'reference'
                    ? 'text-inpay-green-secondary-light-1000'
                    : ''
                )}
              >
                API Reference
              </p>
              {referenceExpanded ? (
                <ChevronUpIcon width={20} height={20} />
              ) : (
                <ChevronDownIcon width={20} height={20} />
              )}
            </div>
            {referenceExpanded && (
              <NavBar
                pages={pages.find((x) => x.id === 'reference')?.pages ?? []}
                parentSection="reference"
                mobileCallback={() => setShowMenu(false)}
              />
            )}
          </div>
          <div className="mobile-subheader flex flex-row items-center justify-between">
            <Link
              onClick={() => setShowMenu(false)}
              to={enhanceLinkWithToken('/support')}
            >
              Support
            </Link>
          </div>
          <OrgDropDown />
          <div className="mobile-subheader flex flex-row items-center justify-end">
            <DarkModeToggle onToggle={() => setShowMenu(false)} />
          </div>
        </div>
      </div>
    </div>
  );
}
